import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiCheckCircle } from "react-icons/fi";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { useTheme } from "../../../ThemeContext";

export const defaultBaseSplitData = [
  {
    id: 1,
    resource: "Anu Keerthana",
    hours: 150,
    rate: 200,
    cost: 30000,
    sharePercent: 12.3,
    earning: 61475.41,
    status: "Provisional",
  },
  {
    id: 2,
    resource: "Ramkumar M",
    hours: 300,
    rate: 300,
    cost: 90000,
    sharePercent: 36.89,
    earning: 184426.23,
    status: "Provisional",
  },
  {
    id: 3,
    resource: "Priya",
    hours: 210,
    rate: 400,
    cost: 84000,
    sharePercent: 34.43,
    earning: 172131.15,
    status: "Provisional",
  },
  {
    id: 4,
    resource: "Balakrishnan M",
    hours: 40,
    rate: 1000,
    cost: 40000,
    sharePercent: 16.39,
    earning: 81967.21,
    status: "Provisional",
  },
];

const getModalDataGridSx = (isDarkMode) => ({
  border: "none !important",
  borderRadius: 0,
  overflow: "hidden",
  "& .MuiDataGrid-main": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
    borderBottom: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
    fontSize: "12.5px",
    fontWeight: 600,
    color: isDarkMode ? "#9ca3af" : "#475569",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
  },
  "& .MuiDataGrid-cell": {
    fontSize: "12.5px",
    borderBottom: isDarkMode ? "1px solid #2d2d2d" : "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    color: isDarkMode ? "#f1f5f9" : "#334155",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
    minHeight: "44px",
  },
});

/**
 * Base Scope Final Allocation Modal (Screen 3 of HTML 2)
 * Allocates pooled base-scope receipts across resources when the project completes.
 */
const BaseScopeAllocationModal = ({
  show,
  onHide,
  onConfirm,
  splitData = defaultBaseSplitData,
}) => {
  const { isDarkMode } = useTheme();
  const [reason, setReason] = useState("");
  const modalDataGridSx = useMemo(() => getModalDataGridSx(isDarkMode), [isDarkMode]);
  const rows = useMemo(() => {
    return splitData.map((r, i) => ({
      id: r.id || r.resource || i,
      ...r,
    }));
  }, [splitData]);

  const columns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => (
          <span className={`fw-medium ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "12.5px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "hours",
        headerName: "Base Hours",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            {Number(params.value).toFixed(1)}
          </span>
        ),
      },
      {
        field: "rate",
        headerName: "Cost Rate",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            ₹{Number(params.value).toLocaleString()}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        width: 120,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            ₹{Number(params.value).toLocaleString()}
          </span>
        ),
      },
      {
        field: "sharePercent",
        headerName: "Share",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span className={isDarkMode ? "text-secondary" : "text-muted"} style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px" }}>
            {Number(params.value).toFixed(2)}%
          </span>
        ),
      },
      {
        field: "earning",
        headerName: "Earning",
        width: 140,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              color: isDarkMode ? "#34d399" : "#16a34a",
              fontSize: "12.5px",
            }}
          >
            ₹{Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
    ],
    [isDarkMode]
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      contentClassName={isDarkMode ? "bg-dark text-white border-secondary" : ""}
    >
      <Modal.Header closeButton className={`border-bottom pb-3 ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`}>
        <div>
          <Modal.Title className={`h6 fw-bold mb-1 ${isDarkMode ? "text-light" : ""}`}>
            Base Scope Run · Final Allocation (₹5,00,000.00)
          </Modal.Title>
          <span className={`badge border ${isDarkMode ? "bg-secondary text-light border-secondary" : "bg-secondary-subtle text-secondary"}`}>
            Base Scope
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className={`p-4 ${isDarkMode ? "bg-dark text-white" : ""}`}>
        {/* KPI Cards */}
        <div className="row g-2 mb-3">
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                RECEIPTS POOLED
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>2</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                RECEIVED
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>₹5,00,000</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                BASE COST
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>₹2,44,000</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                BASE HOURS
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>700.0</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                MARGIN
              </div>
              <div className="fs-5 fw-bold text-success mt-1">2.05x</div>
            </div>
          </div>
        </div>

        <div
          className={`d-flex align-items-center gap-2 py-2 px-3 mb-3 rounded ${
            isDarkMode
              ? "bg-dark text-success border border-success"
              : "alert alert-success border-0"
          }`}
          style={{ fontSize: "12.5px" }}
        >
          <FiCheckCircle size={16} />
          <div>✓ Allocates to exactly ₹5,00,000.00 across the two base-scope receipts.</div>
        </div>

        {/* MUI DataGrid Table */}
        <Box
          className={`rounded overflow-hidden mb-3 border ${isDarkMode ? "border-secondary" : ""}`}
          sx={{ width: "100%", backgroundColor: isDarkMode ? "#1e1e1e" : "#fff" }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            hideFooterPagination
            disableRowSelectionOnClick
            rowHeight={42}
            slots={{
              footer: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
                    backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
                    minHeight: "44px",
                    px: 0,
                    width: "100%",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 200, pl: "16px" }}>Total</Box>
                  <Box
                    sx={{
                      width: 110,
                      minWidth: 110,
                      maxWidth: 110,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    700.0
                  </Box>
                  <Box sx={{ width: 110, minWidth: 110, maxWidth: 110, flexShrink: 0 }} />
                  <Box
                    sx={{
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ₹2,44,000
                  </Box>
                  <Box
                    sx={{
                      width: 110,
                      minWidth: 110,
                      maxWidth: 110,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    100%
                  </Box>
                  <Box
                    sx={{
                      width: 140,
                      minWidth: 140,
                      maxWidth: 140,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      color: isDarkMode ? "#34d399" : "#16a34a",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ₹5,00,000.00
                  </Box>
                </Box>
              ),
            }}
            sx={modalDataGridSx}
          />
        </Box>

        <div className="mb-3">
          <label className={`form-label small fw-semibold ${isDarkMode ? "text-secondary" : "text-muted"}`}>
            REASON (IF PROPORTIONS ADJUSTED)
          </label>
          <input
            type="text"
            className={`form-control form-control-sm ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`}
            placeholder="Enter reason for share adjustment (e.g., rework, delivery speed, scope change)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={`border-top pt-3 ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`}>
        <button
          type="button"
          className="re-btn-cancel"
          style={{ height: "32px", padding: "0 16px", fontSize: "13px" }}
          onClick={onHide}
        >
          Cancel
        </button>
        <button
          type="button"
          className="re-btn-manual"
          style={{ height: "32px", padding: "0 16px", fontSize: "13px" }}
          onClick={onConfirm}
        >
          Confirm Base Scope Run
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BaseScopeAllocationModal;
